import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { agentInstanceService, agentService } from '../services/agentService';
import { getUser } from '../lib/storage';
import ConfirmationDialog from '../components/ui/ConfirmationDialog';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import { departmentService } from '../services/departmentService';

type BackendState = 'provisioning' | 'healthy' | 'degraded' | 'stopped' | 'error' | '';

type FormValues = {
  name: string;
  agentTypeId: string;
  departmentId: string;
  // <-- changed to backend states
  description?: string;
  useImageInChat?: boolean
};

export const Agents: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editAgent, setEditAgent] = useState<any>(null);

  // listing state from API
  const [instances, setInstances] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // confirm dialog
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const user = getUser();
  const ENV_TENANT_ID: any = user?.tenantId

  const [categoryOptions, setCategoryOptions] = useState<{ id: string; name: string }[]>([]);
  const [typeOptions, setTypeOptions] = useState<{ id: string; name: string }[]>([]);

  // react-hook-form
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      name: '',
      agentTypeId: '',
      departmentId: '',
      description: '',
      useImageInChat: false
    },
  });

  useEffect(() => {
    let t = setTimeout(() => {
      loadInstances();
    }, 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const loadDepartments = async () => {
    try {
      const res: any = await departmentService.list();
      const data = res?.departments || [];
      setCategoryOptions(data.map((d: any) => ({ id: String(d.id), name: d.name })));
    } catch {
      toast.error("Failed to load departments");
    }
  };

  useEffect(() => {
    loadDepartments();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res: any = await agentService.listAgentTypes?.();
        const raw = (res && (res.agentTypes || res.data?.agentTypes || res.data || res)) || [];
        const normalized = Array.isArray(raw)
          ? raw.map((t: any) => ({ id: String(t.id), name: String(t.name ?? t.code ?? t.id) }))
          : [];
        if (!cancelled && normalized.length) setTypeOptions(normalized);
      } catch (err) {
        console.warn('Failed to load agent types', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const loadInstances = async () => {
    setLoading(true);
    try {
      const res: any = await agentInstanceService.listAgentInstance?.({ search: searchTerm, includeDeleted: true });
      const raw = (res && (res.data?.agentInstances || res.agentInstances || res.data || res)) || [];

      const normalized = Array.isArray(raw)
        ? raw.map((i: any) => ({
            id: i.id,
            instanceId: i.id,
            name: i.name || i.code || 'Unnamed Agent',
            type: (i.agentType && (i.agentType.name || i.agentType.code)) || 'Unknown',
            typeId: i.agentType?.id || i.agentTypeId || '',
            departmentId: i.departmentId,
            description: i.description || '',
            status: i.isActive ? 'active' : 'inactive',
            state: i.state || '',
            createdAt: i.createdAt,
            updatedAt: i.updatedAt,
            metadata: i.metadata || {},
            tags: (i.department && i.department.name),
            tasksCompleted: i.metrics?.tasksCompleted || 0,
            successRate: i.metrics?.successRate || 0,
            lastActive: i.lastActive || 'unknown',
            color: 'from-agentic-primary-blue to-agentic-primary-blue-light',
            icon: '🤖',
            deletedAt: i?.deletedAt || null,
          }))
        : [];

      setInstances(normalized);
    } catch (err) {
      console.error('Failed to load agent instances', err);
      setInstances([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadInstances(); }, []);

  // after your typeOptions effect
useEffect(() => {
  if (!editAgent) return;
  reset({
    name: editAgent.name || '',
    agentTypeId: String(editAgent.typeId || editAgent.agentTypeId || ''),
    departmentId: String(editAgent.departmentId || ''),
    useImageInChat: editAgent.useImageInChat,
    description: editAgent.description || '',
  });
}, [editAgent, typeOptions, categoryOptions, reset]);


  const openCreate = () => {
    setEditAgent(null);
    reset({
      name: '',
      agentTypeId: typeOptions[0]?.id || '',
      departmentId: categoryOptions[0]?.id || '',
      useImageInChat: false,
      description: '',
    });
    setIsCreateOpen(true);
  };

  const openEdit = (agent: any) => {
    setEditAgent(agent);
    reset({
      name: agent.name || '',
      agentTypeId: String(agent.typeId || agent.agentTypeId || ''),
      departmentId: String(agent.departmentId || ''),
      useImageInChat: agent.useImageInChat,
      description: agent.description || '',
    });
  
    // extra safety:
    setValue('agentTypeId', String(agent.typeId || agent.agentTypeId || ''));
    setIsCreateOpen(true);
  };
  

  const generateCodeFromName = (name: string) => {
    return name.trim().toUpperCase().replace(/\s+/g, '_');
  };

  const onSubmit = async (data: FormValues) => {
    setSaving(true);
    try {
      // allowed backend states
      const allowedStates: BackendState[] = ['provisioning', 'healthy', 'degraded', 'stopped', 'error'];
      const code = generateCodeFromName(data.name || 'AGENT');

      const payload: any = {
        // tenantId: ENV_TENANT_ID,
        name: data.name,
        description: data.description || '',
        isActive: true,
        code,
        departmentId: data.departmentId || undefined,
        agentTypeId: data.agentTypeId || undefined,
        useImageInChat: data?.useImageInChat || false
      };

      if (editAgent && editAgent.instanceId) {
        const res: any = await agentInstanceService.updateAgentInstance(editAgent.instanceId, payload);
        const returned = (res && (res.data || res.agentInstance || res)) || res;
        // merge — ensure state/name/description are preserved from returned response or form
        setInstances(prev => prev.map(i => i.instanceId === editAgent.instanceId ? { ...i, ...returned, name: data.name, description: data.description, useImageInChat: data.useImageInChat || false } : i));
        toast.success(res?.message || 'Agent updated successfully!');
        loadInstances()
      } else {
        const res: any = await agentInstanceService.createAgentInstance?.(payload);
        const created = (res && (res.data || res.agentInstance || res)) || res;
        const newInstance = {
          id: created.id || created.instanceId || `new-${Date.now()}`,
          instanceId: created.id || created.instanceId || `new-${Date.now()}`,
          name: created.name || data.name,
          type: created.agentType?.name || (typeOptions.find(t => t.id === data.agentTypeId)?.name) || 'Custom AI',
          departmentId: created.departmentId || data.departmentId,
          description: created.description || data.description || '',
          status: created.isActive ? 'active' : 'inactive',
          useImageInChat: data?.useImageInChat || false,
          // respect backend state or the one we sent
          tags: created.metadata?.tags || [],
          tasksCompleted: 0,
          successRate: 0,
          lastActive: 'just now',
          color: 'from-agentic-primary-blue to-agentic-primary-blue-light',
          icon: '🤖',
        };
        setInstances(prev => [newInstance, ...prev]);
        toast.success(res?.message || 'Agent created successfully!');
      }

      setIsCreateOpen(false);
      setEditAgent(null);
    } catch (err:any) {
    //   console.log('Failed to save agent instance', err);

  
    // // toast.error(err);
    //   toast.error(err.error||'Failed to save agent.');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (id: string) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const doDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      const res: any = await agentInstanceService.deleteAgentInstance?.(deleteId);
      setInstances(prev => prev.filter(i => i.instanceId !== deleteId && i.id !== deleteId));
      setConfirmOpen(false);
      setDeleteId(null);
      toast.success(res?.message || 'Agent deleted successfully!');
    } catch (err) {
      console.error('Failed to delete agent instance', err);
      toast.error('Failed to delete agent. See console for details.');
    } finally {
      setDeleting(false);
    }
  };

  const filteredAgents = useMemo(() => {
    const base = instances || [];
    return base.filter(agent => {
      const matchesSearch = (agent.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                            (agent.type || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                            (agent.description || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || (agent.status || 'inactive') === statusFilter;
      const matchesCategory = categoryFilter === 'all' || (agent.departmentId || 'all') === categoryFilter;
      return matchesSearch && matchesStatus && matchesCategory;
    });
  }, [instances, searchTerm, statusFilter, categoryFilter]);

  // new helpers to style backend states
  const getStateColor = (state: string) => {
    switch (state) {
      case 'provisioning': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'healthy': return 'bg-green-100 text-green-800 border-green-200';
      case 'degraded': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'stopped': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'error': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStateIcon = (state: string) => {
    switch (state) {
      case 'provisioning': return '⏳';
      case 'healthy': return '✅';
      case 'degraded': return '⚠️';
      case 'stopped': return '⏹️';
      case 'error': return '❌';
      default: return '⚪';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-agentic-white-off via-agentic-primary-blue/5 to-agentic-accent-purple/10">
      {/* Header Section */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-white/30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-3xl font-bold bg-gradient-to-r from-agentic-text-primary via-agentic-primary-blue to-agentic-accent-purple bg-clip-text text-transparent">AI Agents Management</h1>
              <p className="mt-2 text-agentic-text-secondary">Monitor, manage, and optimize your AI agent ecosystem</p>
            </div>

            <div className="mt-4 lg:mt-0 flex items-center space-x-6">
              <button onClick={openCreate} className="px-4 py-2 rounded-xl text-white bg-gradient-to-r from-agentic-primary-blue to-agentic-primary-blue-light hover:shadow-md">+ New Agent</button>
              <div className="text-center">
                <div className="text-2xl font-bold text-agentic-text-primary">{instances.length}</div>
                <div className="text-sm text-agentic-text-secondary">Total Agents</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-agentic-accent-green">{instances.filter(a => a.status === 'active').length}</div>
                <div className="text-sm text-agentic-text-secondary">Active</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-agentic-accent-yellow">{instances.filter(a => a.status === 'learning').length}</div>
                <div className="text-sm text-agentic-text-secondary">Learning</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Controls */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/30 p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-6">
            <div className="flex-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><span className="text-agentic-text-muted">🔍</span></div>
                <input type="text" placeholder="Search agents by name, description, or tags..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent bg-white/90 backdrop-blur-sm" />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
              <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent bg-white/90 backdrop-blur-sm">
                <option value="all">All Departments</option>
                {categoryOptions.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>

              <div className="flex bg-gray-100 rounded-xl p-1">
                <button onClick={() => setViewMode('grid')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${viewMode === 'grid' ? 'bg-white text-agentic-primary-blue shadow-sm' : 'text-agentic-text-secondary hover:text-agentic-text-primary'}`}>Grid</button>
                <button onClick={() => setViewMode('list')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${viewMode === 'list' ? 'bg-white text-agentic-primary-blue shadow-sm' : 'text-agentic-text-secondary hover:text-agentic-text-primary'}`}>List</button>
              </div>
            </div>
          </div>
        </div>

        {/* Agents Grid/List */}
        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAgents.map((agent) => (
              <div key={agent.instanceId || agent.id} className="group relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-agentic-primary-blue/20 to-agentic-accent-purple/20 rounded-3xl blur-lg opacity-0 group-hover:opacity-100 transition duration-500"></div>
                <div className={`relative border border-white/30 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1 rounded-3xl p-6 shadow-xl ${agent?.deletedAt ? "bg-gray-400" : "bg-white/90 backdrop-blur-sm"}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className={`w-12 h-12 bg-gradient-to-br ${agent.color} rounded-2xl flex items-center justify-center shadow-lg`}>
                        <span className="text-white text-xl">{agent.icon}</span>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-agentic-text-primary">{agent.name}</h3>
                        <p className="text-sm text-agentic-text-secondary">{agent.type}</p>
                      </div>
                    </div>
                    <div
                      className={`px-3 py-1 rounded-full text-xs font-semibold border flex items-center ${getStateColor(agent.state)}`}
                    >
                      <span className="mr-1 flex items-center">
                        {getStateIcon(agent.state)}
                      </span>
                      <span>
                        {agent?.state
                          ? agent.state.charAt(0).toUpperCase() +
                            agent.state.slice(1).toLowerCase()
                          : '—'}
                      </span>
                    </div>

                  </div>

                  <p className="text-sm text-agentic-text-secondary mb-4 line-clamp-2">{agent.description}</p>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="text-center p-3 bg-gradient-to-br from-agentic-gray-50 to-agentic-gray-100 rounded-xl">
                      <div className="text-lg font-bold text-agentic-text-primary">{(agent.tasksCompleted || 0).toLocaleString()}</div>
                      <div className="text-xs text-agentic-text-secondary">Tasks</div>
                    </div>
                    <div className="text-center p-3 bg-gradient-to-br from-agentic-gray-50 to-agentic-gray-100 rounded-xl">
                      <div className="text-lg font-bold text-agentic-accent-green">{0}%</div>
                      <div className="text-xs text-agentic-text-secondary">Interaction</div>
                    </div>
                  </div>

                 <div className="flex flex-wrap items-center gap-3 mb-4">
  {/* Agent Type */}
  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 text-xs font-medium border border-blue-100">
    <span className="opacity-70">Agent Type:</span>
    <span>{agent?.type}</span>
  </div>

  {/* Department */}
  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-50 text-slate-700 text-xs font-medium border border-slate-200">
    <span className="opacity-70">Dept:</span>
    <span>
      {categoryOptions?.find(c => c.id === agent.departmentId)?.name}
    </span>
  </div>
</div>


                  <div className="flex space-x-2">
                    <button onClick={() => navigate(`/agents/${agent.instanceId || agent.id}/config`)} className="flex-1 px-4 py-2 bg-gradient-to-r from-agentic-primary-blue to-agentic-primary-blue-light text-white text-sm font-medium rounded-xl hover:shadow-lg transition-all duration-200">Configure</button>
                    {agent.deletedAt ? <></> : <>
                    <button onClick={() => openEdit(agent)} className="px-4 py-2 bg-gradient-to-r from-agentic-accent-yellow to-agentic-accent-yellow text-white text-sm font-medium rounded-xl hover:shadow-lg transition-all duration-200">Edit</button>
                    <button onClick={() => confirmDelete(agent.instanceId || agent.id)} className="px-3 py-2 bg-gradient-to-r from-agentic-accent-red to-agentic-accent-red text-white text-sm font-medium rounded-xl hover:shadow-lg transition-all duration-200">Delete</button>
                    </>}
                  </div>

                  <div className="mt-3 text-xs text-agentic-text-muted text-center">Last updated: {agent.updatedAt ? new Date(agent.updatedAt).toLocaleString() : '—'}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bgF-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/30 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-agentic-gray-50 to-agentic-gray-100">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-agentic-text-secondary uppercase tracking-wider">Agent</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-agentic-text-secondary uppercase tracking-wider">State</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-agentic-text-secondary uppercase tracking-wider">Tasks</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-agentic-text-secondary uppercase tracking-wider">Interaction</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-agentic-text-secondary uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredAgents.map((agent) => (
                    <tr key={agent.instanceId || agent.id} className="hover:bg-agentic-gray-50/50 transition-colors duration-200">
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <div className={`w-10 h-10 bg-gradient-to-br ${agent.color} rounded-xl flex items-center justify-center shadow-sm`}>
                            <span className="text-white text-lg">{agent.icon}</span>
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-agentic-text-primary">{agent.name}</div>
                            <div className="text-xs text-agentic-text-secondary">Agent Type: {agent.type}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${getStateColor(agent.state)}`}>
                          <span className="mr-1">{getStateIcon(agent.state)}</span>
                          <span>{agent?.state}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <div className="font-semibold text-agentic-text-primary">{(agent.tasksCompleted || 0).toLocaleString()} tasks</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-agentic-text-secondary">{0}</td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-2">
                          <button onClick={() => navigate(`/agents/${agent.instanceId || agent.id}/config`)} className="px-3 py-1 bg-agentic-primary-blue text-white text-xs font-medium rounded-lg hover:bg-agentic-primary-blue-dark transition-colors duration-200">Configure</button>
                          <button onClick={() => openEdit(agent)} className="px-3 py-1 bg-agentic-gray-100 text-agentic-text-primary text-xs font-medium rounded-lg">Edit</button>
                          <button onClick={() => confirmDelete(agent.instanceId || agent.id)} className="px-3 py-1 bg-gradient-to-r from-agentic-accent-red to-agentic-accent-red text-white text-xs font-medium rounded-lg">Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty State */}
        {filteredAgents.length === 0 && !loading && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🤖</div>
            <h3 className="text-xl font-semibold text-agentic-text-primary mb-2">No agents found</h3>
            <p className="text-agentic-text-secondary">Try adjusting your search criteria or filters.</p>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {isCreateOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" onClick={() => { setIsCreateOpen(false); setEditAgent(null); }}></div>
          <div className="relative w-full max-w-3xl mx-4 bg-white rounded-2xl shadow-xl border border-gray-100 p-6 max-h-[85vh] overflow-y-auto">
            <div className="mb-4">
              <h2 className="text-xl font-semibold text-agentic-text-primary">{editAgent ? 'Edit Agent' : 'Create AI Agent'}</h2>
              <p className="text-sm text-agentic-text-secondary mt-1">Define or update an agent instance.</p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-agentic-text-primary mb-1">Name <span className="text-red-500"> *</span></label>
                  <input
                    {...register('name', { required: 'Name is required', minLength: { value: 2, message: 'Name must be at least 2 characters' } })}
                    placeholder="e.g., Research Agent"
                    className={`w-full px-3 py-2 rounded-lg border ${errors.name ? 'border-red-300' : 'border-gray-200'} focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent`}
                  />
                  {errors.name && <div className="text-xs text-red-600 mt-1">{errors.name.message}</div>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-agentic-text-primary mb-1">Agent Type<span className="text-red-500"> *</span></label>
                  <select
                    {...register('agentTypeId', { required: 'Agent type is required' })}
                    className={`w-full px-3 py-2 rounded-lg border ${errors.agentTypeId ? 'border-red-300' : 'border-gray-200'} focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent`}
                  >
                    <option value="">-- Select agent type --</option>
                    {typeOptions.length === 0 ? <option value="">-- No types available --</option> : typeOptions.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                  {errors.agentTypeId && <div className="text-xs text-red-600 mt-1">{errors.agentTypeId.message}</div>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-agentic-text-primary mb-1">Department<span className="text-red-500"> *</span></label>
                  <select
                    {...register('departmentId', { required: 'Department is required' })}
                    className={`w-full px-3 py-2 rounded-lg border ${errors.departmentId ? 'border-red-300' : 'border-gray-200'} focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent`}
                  >
                    <option value="">-- Select department --</option>
                    {categoryOptions.length === 0 ? <option value="">-- No categories available --</option> : categoryOptions.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  {errors.departmentId && <div className="text-xs text-red-600 mt-1">{errors.departmentId.message}</div>}
                </div> 
              <div className='mt-6'>
                  <label className="flex items-center gap-3 p-2 border rounded hover:bg-gray-50 cursor-pointer rounded-md">
                    <input
                      type="checkbox"
                      {...register("useImageInChat")}
                      onChange={(e) => {
                        setValue("useImageInChat", e.target.checked); // updates form value
                      }}
                      className="h-4 w-4"
                    />

                    <div>
                      <div className="font-medium text-sm">
                        Use image in chat<span className="text-red-500"> *</span>
                      </div>
                    </div>
                  </label>

                  {errors.useImageInChat && (
                    <div className="text-xs text-red-600 mt-1">{errors.useImageInChat.message}</div>
                  )}
              </div>

              </div>

              <div>
                <label className="block text-sm font-medium text-agentic-text-primary mb-1">Description</label>
                <textarea {...register('description')} placeholder="Short description of this agent" rows={4} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent" />
                {/* description intentionally not validated */}
              </div>

              <div className="flex items-center justify-end space-x-3 pt-2">
                <button type="button" onClick={() => { setIsCreateOpen(false); setEditAgent(null); }} className="px-4 py-2 rounded-lg border border-gray-200 text-agentic-text-secondary hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg text-white bg-gradient-to-r from-agentic-primary-blue to-agentic-primary-blue-light hover:shadow-md">{saving ? 'Saving...' : (editAgent ? 'Update' : 'Create')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmationDialog
        isOpen={confirmOpen}
        onClose={() => { setConfirmOpen(false); setDeleteId(null); }}
        onConfirm={doDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this agent instance?"
        confirmText="Delete"
        cancelText="Cancel"
        loading={deleting}
        variant="error"
      />
    </div>
  );
};

export default Agents;
